import { isEmpty } from 'bold-ui'
import { FormProps } from 'components/form'
import { useProcedimentoSelectFieldQuery } from 'graphql/hooks.generated'
import { GrupoProcedimentoEnum, TipoExameEnum } from 'graphql/types.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { partition } from 'lodash'
import { useParams } from 'react-router'
import { isUndefinedOrNull } from 'util/checks'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import { SolicitacaoExameQueryModel } from '../../objetivo/resultados-exames/model-resultadosExames'
import { showCodigoProcedimento } from '../../objetivo/resultados-exames/util-resultadosExames'
import { SolicitacaoExameModel } from './model-solicitacaoExames'
import SolicitarExamesFormModal, { SolicitarExamesUrlParams } from './SolicitarExamesFormModal'

interface SolicitarExamesReplicarModalProps extends Omit<FormProps<SolicitacaoExameModel>, 'initialValues' | 'render'> {
  cidadao: CidadaoAtendimento
  idsExmAdicionadosAtend: Set<ID>
  existentes: ReadonlyArray<SolicitacaoExameQueryModel>
  onClose(): void
}

export default function SolicitarExamesReplicarModal(props: SolicitarExamesReplicarModalProps) {
  const { cidadao, onSubmit, onClose, existentes, idsExmAdicionadosAtend } = props

  const { id } = useParams<SolicitarExamesUrlParams>()
  const { atendimentoProfissional } = useAtendimentoContext()

  const solicitacao = existentes?.find((item) => item.id === id)
  const foundSolicitacaoExistente = !isUndefinedOrNull(solicitacao)
  const examesIds = solicitacao?.exames?.flatMap((resultado) => resultado.exame.id) ?? []
  const tipoExame = solicitacao?.tipoExame
  const gruposProcedimento =
    tipoExame === TipoExameEnum.OCI
      ? [GrupoProcedimentoEnum.OFERTAS_CUIDADOS_INTEGRADOS]
      : [GrupoProcedimentoEnum.FINALIDADE_DIAGNOSTICA]

  const { data, loading } = useProcedimentoSelectFieldQuery({
    skip: !foundSolicitacaoExistente,
    variables: {
      input: {
        ids: examesIds,
        filtro: {
          tipoExame,
          gruposProcedimento,
          sexo: isEmpty(cidadao?.identidadeGeneroDbEnum) ? cidadao?.sexo : null,
          dataNascimento: cidadao?.dataNascimento,
          dataAtendimento: atendimentoProfissional.iniciadoEm,
        },
        pageParams: {
          fetchPageInfo: false,
          size: examesIds.length,
        },
      },
    },
  })

  const procedimentosValidosIds = data?.procedimentos?.content
    ?.filter((item) => examesIds?.includes(item.id))
    ?.flatMap((item) => item.id)

  const [examesValidos, examesInvalidos] = partition(solicitacao?.exames, (item) =>
    procedimentosValidosIds?.includes(item.exame.id)
  )

  const alertMessage = getExamesInvalidosMessage(examesInvalidos, tipoExame)

  const initialValues = {
    tipoExame,
    examesSolicitados: examesValidos.map((item) => ({
      _id: item.exame.id,
      exame: item.exame,
    })),
  } as SolicitacaoExameModel

  return (
    foundSolicitacaoExistente &&
    !loading && (
      <SolicitarExamesFormModal
        cidadao={cidadao}
        editing={false}
        initialValues={initialValues}
        alertMessage={alertMessage}
        iniciadoEm={atendimentoProfissional.iniciadoEm.getTime()}
        idsExmAdicionadosAtend={idsExmAdicionadosAtend}
        onSubmit={onSubmit}
        onClose={onClose}
      />
    )
  )
}

const getExamesInvalidosMessage = (examesInvalidos: SolicitacaoExameQueryModel['exames'], tipoExame: TipoExameEnum) => {
  if (isEmpty(examesInvalidos)) return null
  const entityName = tipoExame === TipoExameEnum.OCI ? 'procedimento' : 'exame'

  return (examesInvalidos.length > 1 ? `Os ${entityName}s` : `O ${entityName}`)
    .concat(
      examesInvalidos.map((item) => ` ${item.exame.descricao} - ${showCodigoProcedimento(item.exame.codigo)}`).join()
    )
    .concat(examesInvalidos.length > 1 ? ' não serão replicados.' : ' não será replicado.')
}
